import React, { useEffect, useState } from "react";
import { Button, message } from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import InventoryForm from "./InventoryForm";
import API from "../../api"; // Your API instance
import CustomTable from "../../components/Table/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal";

export default function Inventory() {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [inventoryToDelete, setInventoryToDelete] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({ currentPage: 1, pageSize: 5 });

  // Fetch inventory data from the API
  const getInventory = async () => {
    try {
      const response = await API.get("/inventory");
      setInventory(response?.data);
    } catch (err) {
      console.error("Error fetching inventory:", err);
      message.error("Failed to fetch inventory. Please try again.");
    }
  };

  useEffect(() => {
    getInventory();
  }, []);

  const handleAddOrEditInventory = async (data) => {
    try {
      if (isEdit) {
        // Update Inventory
        await API.put(`/inventory/${selectedInventory?._id}`, data);
        message.success("Inventory updated successfully!");
      } else {
        // Add new Inventory
        await API.post("/inventory", data);
        message.success("Inventory added successfully!");
      }
      getInventory(); // Refresh the Inventory list
      setOpenDrawer(false); // Close drawer
    } catch (error) {
      console.error("Error saving Inventory:", error);
      message.error(error?.response?.data?.message);
    }
  };

  const handleEditClick = (record) => {
    setIsEdit(true);
    setSelectedInventory({
      ...record,
      storeId: record?.storeId, // Extract store ID
      // productId: record.productId, // Extract product ID
      productsInInventory: record?.products,
    });
    setOpenDrawer(true);
  };

  const handleDeleteClick = (record) => {
    setInventoryToDelete(record);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteInventory = async () => {
    try {
      await API.delete(`/inventory/${inventoryToDelete?._id}`);
      setInventory((prev) =>
        prev.filter((item) => item?._id !== inventoryToDelete?._id)
      );
      setIsDeleteModalVisible(false);
      message.success("Inventory deleted successfully!");
    } catch (err) {
      console.error("Error deleting inventory:", err);
      message.error(err.response?.data?.message);
    }
  };

  const columns = [
    {
      title: "Index",
      key: "index",
      render: (_, __, index) =>
        (paginationInfo.currentPage - 1) * paginationInfo.pageSize + index + 1,
    },
    {
      title: "Branch Name",
      dataIndex: ["storeId", "name"],
      key: "storeId",
    },

    // {
    //   title: "Branch Address",
    //   render: (_, order) => order.storeId.address, // Display store address
    //   key: "storeAddress",
    // },

    {
      title: "Products",
      render: (_, order) => (
        <ul aria-atomic>
          {order.products.map((product) => (
            <li key={product?._id}>
              {product?.productId?.name} (Qty: {product?.quantity})
            </li>
          ))}
        </ul>
      ),
      key: "products",
    },
    {
      title: "Total Amount",
      dataIndex: "stockLevel",
      key: "stockLevel",
    },
    {
      title: "Actions",
      align: "center",
      key: "actions",
      render: (record) => (
        <div>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditClick(record)}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            style={{ marginLeft: 8 }}
            onClick={() => handleDeleteClick(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setIsEdit(false);
            setSelectedInventory(null); // Clear selected inventory for add mode
            setOpenDrawer(true);
          }}
        >
          Add New Inventory
        </Button>
      </div>

      {/* Inventory Form Drawer */}
      {openDrawer && (
        <InventoryForm
          open={openDrawer}
          setOpen={setOpenDrawer}
          isEdit={isEdit}
          initialValue={isEdit ? selectedInventory : null}
          onSubmit={handleAddOrEditInventory}
        />
      )}

      {/* Inventory Table */}
      <CustomTable
        columns={columns}
        dataSource={inventory}
        onPaginationChange={(pagination) =>
          setPaginationInfo({ currentPage: pagination.current, pageSize: pagination.pageSize })
      }
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={isDeleteModalVisible}
        onConfirm={handleDeleteInventory}
        onCancel={() => setIsDeleteModalVisible(false)}
        title="Confirm Deletion"
        content="Are you sure you want to delete this inventory item? This action cannot be undone."
      />
    </div>
  );
}
